import React, { JSX } from 'react';
import classNames from 'classnames';
import { ReactComponent as ArrowIcon } from '@/assets/arrow-down.svg';
import { styled } from '@mui/material/styles';

export type ICollapsibleProps = Omit<
	JSX.IntrinsicElements['article'],
	'children'
> & {
	isOpen?: boolean;
	toggle?: (e: HTMLElement) => void;
	softHide?: boolean;
	header?: React.ReactNode;
	headerProps?: Omit<JSX.IntrinsicElements['header'], 'children'>;
	divider?: boolean;
	dividerProps?: Omit<JSX.IntrinsicElements['div'], 'children'>;
	mainProps?: JSX.IntrinsicElements['main'];
	children?: React.ReactNode;
};

export const Collapsible = styled(
	({
		className,
		children,
		isOpen,
		toggle,
		softHide = true,
		header,
		headerProps,
		divider = true,
		dividerProps,
		mainProps,
		...props
	}: ICollapsibleProps) => {
		const { className: headerClassName, ...headerRest } = headerProps ?? {};
		const { className: dividerClassName, ...dividerRest } = dividerProps ?? {};
		const {
			className: mainClassName,
			children: mainChildren,
			...mainRest
		} = mainProps ?? {};

		return (
			<article
				className={classNames(
					'collapsible',
					isOpen && `collapsible--open`,
					className,
				)}
				{...props}
			>
				<header
					className={classNames(
						'collapsible__header',
						toggle && 'collapsible__header--toggable',
						headerClassName,
					)}
					onClick={(e) => {
						if (toggle) e.currentTarget.scrollIntoView({ block: 'center' });
						toggle?.(e.currentTarget);
					}}
					{...headerRest}
				>
					<div className="collapsible__title">{header}</div>
					{toggle && <ArrowIcon className="collapsible__toggle" />}
				</header>

				{divider && (
					<div
						className={classNames('collapsible__divider', dividerClassName)}
						{...dividerRest}
					/>
				)}

				<main
					className={classNames('collapsible__main', mainClassName)}
					{...mainRest}
				>
					{softHide || isOpen ? (children ?? mainChildren) : null}
				</main>
			</article>
		);
	},
	{ label: 'Collapsible', target: 'Collapsible-root' },
)({
	'& .collapsible__header': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexWrap: 'nowrap',
		padding: '0.6em 0.9em 0.6em 0.9em',
		fontSize: '1.5em',
		color: 'var(--message__collapsible__header_color)',
		background: 'var(--message__collapsible__header_background)',
		'&.collapsible__header--toggable': {
			cursor: 'pointer',
			'&:hover': {
				background: 'var(--message__collapsible__header--hover_background)',
			},
		},
		'& p': {
			margin: 0,
		},
	},
	'& .collapsible__divider': {
		display: 'none',
		height: '1px',
		backgroundColor: 'var(--message__collapsible__divider_background)',
		marginLeft: '1.4em',
	},
	'& .collapsible__main': {
		padding: '1.4em',
	},
	'& .collapsible__title': {
		display: 'flex',
		flex: 1,
		overflow: 'hidden',
	},
	'& .collapsible__toggle': {
		width: '15px',
		height: '15px',
		fill: 'var(--message__collapsible__toggle_color)',
	},
	'&:not(.collapsible--open) .collapsible__main': {
		display: 'none',
	},
	'&.collapsible--open .collapsible__divider': {
		display: 'block',
	},
	'&.collapsible--open .collapsible__toggle': {
		transform: 'rotate(180deg)',
	},
});
